import {forwardRef, useEffect, useImperativeHandle, useRef, useState, type Ref} from 'react'
import {useTranslation} from 'react-i18next'

type ReplayerLike = {
  play: (from?: number) => void
  pause: (at?: number) => void
  getCurrentTime: () => number
  getMetaData: () => {totalTime: number}
  setConfig: (config: {speed: number}) => void
  on: (event: string, cb: () => void) => void
}

/** From `{replayId}.markers.json`; `id` matches the section's `operaciones[].id`. */
type ReplayMarker = {id: string; tMs: number}

/** rrweb's FullSnapshot event (type 4) carries the recorded viewport; `getMetaData()` does not. */
function recordedViewport(events: unknown[]): {width: number; height: number} {
  const meta = (events as Array<{type?: number; data?: {width?: number; height?: number}}>).find((e) => e.type === 4)
  return {width: meta?.data?.width ?? 1280, height: meta?.data?.height ?? 800}
}

const SPEEDS = [0.5, 0.75, 1, 1.5, 2] as const
type Speed = (typeof SPEEDS)[number]
const DEFAULT_SPEED: Speed = 1.5

function formatTime(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`
}

function chapterAt(currentTime: number, markers: ReplayMarker[]): string | null {
  let active: string | null = null
  for (const m of markers) {
    if (m.tMs <= currentTime) active = m.id
    else break
  }
  return active
}

/** A ref, not a prop: a one-shot «seek to this chapter now» has no clean prop form. */
export type ReplayPlayerHandle = {
  seekToChapter: (id: string) => void
}

/** The ▶ cover gates the fetch, so viewing a section never pulls the stream. The markers file
 * is best-effort: a 404 just means an unchaptered replay. */
function ReplayPlayerImpl(
  {
    replayId,
    title,
    chapterTitles,
    onChapterChange,
  }: {
    replayId: string
    title: string
    chapterTitles?: Record<string, string>
    onChapterChange?: (markerId: string | null) => void
  },
  ref: Ref<ReplayPlayerHandle>
) {
  const {t} = useTranslation()
  const [started, setStarted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [speed, setSpeed] = useState<Speed>(DEFAULT_SPEED)
  const [currentTime, setCurrentTime] = useState(0)
  const [totalTime, setTotalTime] = useState(0)
  const [markers, setMarkers] = useState<ReplayMarker[]>([])
  const stageRef = useRef<HTMLDivElement>(null)
  const replayerRef = useRef<ReplayerLike | null>(null)
  const rafRef = useRef(0)

  useEffect(() => {
    if (!started || !stageRef.current) return
    let cancelled = false
    let cleanupFit: (() => void) | null = null
    setLoading(true)
    setError(false)

    async function mount() {
      try {
        const [{Replayer}, resp, markersResp] = await Promise.all([
          import('@rrweb/replay'),
          fetch(`/manual/replays/${replayId}.json`),
          fetch(`/manual/replays/${replayId}.markers.json`).catch(() => null),
          import('@rrweb/replay/dist/style.css'),
        ])
        if (!resp.ok) throw new Error('replay stream not found')
        const events = await resp.json()
        if (cancelled || !Array.isArray(events) || !events.length) throw new Error('empty replay stream')
        if (!stageRef.current) return
        if (markersResp?.ok) {
          const parsed = await markersResp.json().catch(() => null)
          if (!cancelled && Array.isArray(parsed)) setMarkers(parsed)
        }

        const replayer = new Replayer(events, {
          root: stageRef.current,
          speed: DEFAULT_SPEED,
          skipInactive: false,
          mouseTail: false,
          showWarning: false,
          useVirtualDom: true,
        }) as unknown as ReplayerLike
        replayerRef.current = replayer
        const meta = replayer.getMetaData()
        setTotalTime(meta.totalTime)

        const viewport = recordedViewport(events)
        const stage = stageRef.current
        // El stage manda (aspect-ratio grabado, tope 65vh) y la grabación se escala a contain.
        stage.style.aspectRatio = `${viewport.width} / ${viewport.height}`
        stage.style.maxHeight = '65vh'
        // Centrado por flex: el wrapper conserva su caja sin escalar (`flex: 0 0 auto`, así
        // flexbox no la colapsa) y el scale actúa desde el centro.
        stage.style.display = 'flex'
        stage.style.alignItems = 'center'
        stage.style.justifyContent = 'center'
        function fit() {
          const wrapper = stage.querySelector<HTMLElement>('.replayer-wrapper')
          if (!wrapper || !stage.clientWidth) return
          const scale = Math.min(stage.clientWidth / viewport.width, stage.clientHeight / viewport.height)
          wrapper.style.flex = '0 0 auto'
          wrapper.style.transformOrigin = 'center center'
          wrapper.style.transform = `scale(${scale})`
        }
        fit()
        // `.replayer-wrapper` lands after `new Replayer(...)` returns, so the synchronous fit()
        // can miss it: retry once after a frame.
        requestAnimationFrame(fit)
        const resizeObserver = new ResizeObserver(fit)
        resizeObserver.observe(stage)
        cleanupFit = () => resizeObserver.disconnect()

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        replayer.on('finish', () => {
          setPlaying(false)
          setCurrentTime(meta.totalTime)
        })
        replayer.on('resize', fit)
        if (!reduceMotion) {
          replayer.play(0)
          setPlaying(true)
        }
        setLoading(false)
      } catch {
        if (!cancelled) {
          setError(true)
          setLoading(false)
        }
      }
    }

    void mount()
    return () => {
      cancelled = true
      cancelAnimationFrame(rafRef.current)
      cleanupFit?.()
    }
  }, [started, replayId])

  useEffect(() => {
    if (!playing) return
    function tick() {
      const replayer = replayerRef.current
      if (!replayer) return
      setCurrentTime(Math.min(replayer.getCurrentTime(), totalTime))
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [playing, totalTime])

  useEffect(() => {
    if (!markers.length) return
    onChapterChange?.(chapterAt(currentTime, markers))
  }, [currentTime, markers, onChapterChange])

  function togglePlay() {
    const replayer = replayerRef.current
    if (!replayer) return
    if (playing) {
      replayer.pause()
      setPlaying(false)
    } else {
      const from = currentTime >= totalTime ? 0 : currentTime
      replayer.play(from)
      setPlaying(true)
    }
  }

  function handleSeek(value: number) {
    const replayer = replayerRef.current
    if (!replayer) return
    replayer.pause(value)
    setPlaying(false)
    setCurrentTime(value)
  }

  function handleSpeed(next: Speed) {
    setSpeed(next)
    replayerRef.current?.setConfig({speed: next})
  }

  useImperativeHandle(
    ref,
    () => ({
      seekToChapter(id: string) {
        const target = markers.find((m) => m.id === id)
        if (target) handleSeek(target.tMs)
      },
    }),
    [markers]
  )

  if (!started) {
    return (
      <div
        className="surface flex flex-col items-center justify-center gap-3 overflow-hidden rounded-xl border border-border p-10 text-center"
        data-testid="replay-cover">
        <p className="font-heading text-base text-foreground">{title}</p>
        <p className="max-w-sm text-sm text-muted-foreground">{t('manual.replay.coverHint')}</p>
        <button
          type="button"
          onClick={() => setStarted(true)}
          aria-label={t('manual.replay.play')}
          data-testid="replay-play-cover"
          className="mt-2 flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-primary text-xl text-primary-foreground shadow-pop outline-none focus-visible:ring-2 focus-visible:ring-ring/50 hover:bg-primary/90">
          ▶
        </button>
      </div>
    )
  }

  if (error) {
    return (
      <div
        className="surface rounded-xl border border-border p-6 text-center text-sm text-muted-foreground"
        data-testid="replay-error">
        {t('manual.replay.unavailable')}
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border shadow-pop" data-testid="replay-player">
      <div
        ref={stageRef}
        aria-label={t('manual.replay.stageLabel', {title})}
        className="relative min-h-40 overflow-hidden bg-white [&_.replayer-wrapper]:relative"
      />
      {loading && (
        <p className="px-4 py-3 text-center text-sm text-muted-foreground" data-testid="replay-loading">
          {t('manual.replay.loading')}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3 border-t border-border bg-secondary/40 px-4 py-2.5">
        <button
          type="button"
          onClick={togglePlay}
          disabled={loading}
          aria-label={playing ? t('manual.replay.pause') : t('manual.replay.play')}
          data-testid="replay-toggle"
          className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md border border-border bg-background text-primary outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50">
          {playing ? '❚❚' : '▶'}
        </button>
        <div className="relative min-w-24 flex-1">
          <input
            type="range"
            min={0}
            max={totalTime || 100}
            step={1}
            value={currentTime}
            onChange={(e) => handleSeek(Number(e.target.value))}
            disabled={loading}
            aria-label={t('manual.replay.progress')}
            data-testid="replay-seek"
            className="h-1 w-full cursor-pointer accent-primary"
          />
          {totalTime > 0 && markers.length > 0 && (
            <div className="pointer-events-none absolute inset-x-0 top-1/2 h-1 -translate-y-1/2">
              {markers.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => handleSeek(m.tMs)}
                  disabled={loading}
                  title={chapterTitles?.[m.id] ?? m.id}
                  aria-label={chapterTitles?.[m.id] ?? m.id}
                  data-testid={`replay-chapter-${m.id}`}
                  className="pointer-events-auto absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full border border-background bg-primary/70 outline-none hover:bg-primary focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50"
                  style={{left: `${Math.min(100, Math.max(0, (m.tMs / totalTime) * 100))}%`}}
                />
              ))}
            </div>
          )}
        </div>
        <span className="shrink-0 font-mono text-xs text-muted-foreground tabular-nums">
          {formatTime(currentTime)} / {formatTime(totalTime)}
        </span>
        <span className="flex shrink-0 gap-1">
          {SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => handleSpeed(s)}
              disabled={loading}
              aria-pressed={speed === s}
              aria-label={t('manual.replay.speedLabel', {speed: s})}
              data-testid={`replay-speed-${s}`}
              className="cursor-pointer rounded-md border border-border bg-background px-2 py-1 font-mono text-[11px] font-semibold text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/50 aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground disabled:opacity-50">
              {s}×
            </button>
          ))}
        </span>
      </div>
    </div>
  )
}

export const ReplayPlayer = forwardRef(ReplayPlayerImpl)
